import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    afterFreeTrialEnds: (expirationDate: Date) => string;
    description: string;
    quanity: string;
    unitPrice: string;
    amount: string;
    subtotal: string;
    discountApplied: (percent: number) => string;
    price: (formattedPrice: string) => string;
    pricePeriodMonthly: string;
    pricePeriodYearly: string;
    memberIncludedSingular: string;
    membersIncludedPlural: (count: number) => string;
    addOnTitle: (label: string) => string;
    storageDescriptionSizeOnly: (size: string) => string;
    storageDescriptionSizeWithDiscount: (size: string, discount: string) => string;
    billedMonthly: string;
    billedYearly: string;
    tax: string;
    dueNowWithAsterisk: string;
    dueNowRegular: string;
    planTitle: (title: string) => string;
    planDescriptionMembersOnly: (members: string) => string;
    planDescriptionMembersWithDiscount: (members: string, discount: string) => string;
    planDescriptionDiscountOnly: (discount: string) => string;
    taxPercentVAT: (taxPercent: number, billingCountry: string) => string;
    taxPercentOther: (taxPercent: number, displayName: string) => string;
    noTaxChargedVAT: string;
    noTaxChargedSalesTaxExcluded: string;
    noTaxChargedSalesTaxZero: string;
    total: string;
    toolTip: string;
    descriptionCollapsedFormat: (description: string) => string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Label shown when free trial ends - includes trial end date */
            afterFreeTrialEnds: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (expirationDate: Date): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.afterFreeTrialEnds?c=3vsfkehodgdim", defaultMessage: "After free trial ends {expirationDate, date, medium}" }, { expirationDate: expirationDate }));
                },
                enumerable: true,
                configurable: true
            },
            /** Table column header for item descriptions */
            description: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.description?c=2ehin4brfeo08", defaultMessage: "Description" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Table column header for quantity (Note: typo in original) */
            quanity: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.quanity?c=1z2iqualmkg2o", defaultMessage: "Qty" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Table column header for unit price */
            unitPrice: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.unitPrice?c=2panm3evmruuf", defaultMessage: "Unit price" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Table column header for total amount */
            amount: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.amount?c=245fhwuuexu4r", defaultMessage: "Amount" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Row label for subtotal before tax */
            subtotal: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.subtotal?c=3lkk98lim3p3z", defaultMessage: "Subtotal" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Discount text - standalone */
            discountApplied: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (percent: number): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.discountApplied?c=m3cnsyy9g5i7", defaultMessage: "{percent}% off applied" }, { percent: percent }));
                },
                enumerable: true,
                configurable: true
            },
            /** Format price display - pass pre-formatted price */
            price: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (formattedPrice: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.price?c=1a8wnxlzwtw6y", defaultMessage: "{formattedPrice}" }, { formattedPrice: formattedPrice }));
                },
                enumerable: true,
                configurable: true
            },
            /** Period suffix - monthly */
            pricePeriodMonthly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.pricePeriodMonthly?c=134739966crw0", defaultMessage: "/mo." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Period suffix - yearly */
            pricePeriodYearly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.pricePeriodYearly?c=2ht1nzmxozwmo", defaultMessage: "/yr." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Member count - singular */
            memberIncludedSingular: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.memberIncludedSingular?c=svwbg94n9ndn", defaultMessage: "1 Member included" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Member count - plural */
            membersIncludedPlural: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.membersIncludedPlural?c=3gf7cxlbjbsew", defaultMessage: "{count} Members included" }, { count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Add-on title format */
            addOnTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (label: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.addOnTitle?c=3nwmeclgz7r2k", defaultMessage: "{label} add-on" }, { label: label }));
                },
                enumerable: true,
                configurable: true
            },
            /** Storage add-on description - size only */
            storageDescriptionSizeOnly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (size: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.storageDescriptionSizeOnly?c=21cwlkngg1ey7", defaultMessage: "{size} each" }, { size: size }));
                },
                enumerable: true,
                configurable: true
            },
            /** Storage add-on description - size with discount */
            storageDescriptionSizeWithDiscount: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (size: string, discount: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.storageDescriptionSizeWithDiscount?c=3e13xb9klrws9", defaultMessage: "{size} each \u2022 {discount}" }, { size: size, discount: discount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Billing frequency - monthly */
            billedMonthly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.billedMonthly?c=3etdblcino1ol", defaultMessage: "Billed monthly" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Billing frequency - yearly */
            billedYearly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.billedYearly?c=3n787lyz1lyc0", defaultMessage: "Billed yearly" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tax row label */
            tax: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.tax?c=2arxis3jm8zcp", defaultMessage: "Tax" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Due now label - with asterisk if immediate payment required */
            dueNowWithAsterisk: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.dueNowWithAsterisk?c=33f8ssgtevtwn", defaultMessage: "Due now*" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Due now label - without asterisk */
            dueNowRegular: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.dueNowRegular?c=2g3bt3b5stp01", defaultMessage: "Due now" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Plan title */
            planTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (title: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.planTitle?c=3ov9zzozxe7xt", defaultMessage: "{title} plan" }, { title: title }));
                },
                enumerable: true,
                configurable: true
            },
            /** Plan description - members only (no discount) */
            planDescriptionMembersOnly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (members: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.planDescriptionMembersOnly?c=3ehbskpsa6mgt", defaultMessage: "{members}" }, { members: members }));
                },
                enumerable: true,
                configurable: true
            },
            /** Plan description - members with discount */
            planDescriptionMembersWithDiscount: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (members: string, discount: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.planDescriptionMembersWithDiscount?c=1qql6256wb3uo", defaultMessage: "{members} \u2022 {discount}" }, { members: members, discount: discount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Plan description - discount only (no members) */
            planDescriptionDiscountOnly: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (discount: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.planDescriptionDiscountOnly?c=1jh46y5wzucty", defaultMessage: "{discount}" }, { discount: discount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tax percentage with country - for VAT countries */
            taxPercentVAT: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (taxPercent: number, billingCountry: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.taxPercentVAT?c=8j8zyh5zx3eu", defaultMessage: "VAT \u2022 {billingCountry} ({taxPercent}%)" }, { billingCountry: billingCountry, taxPercent: taxPercent }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tax percentage with display name - for non-VAT regions */
            taxPercentOther: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (taxPercent: number, displayName: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.taxPercentOther?c=1uzh5u3b5l5fr", defaultMessage: "{displayName} \u2022 ({taxPercent}%)" }, { displayName: displayName, taxPercent: taxPercent }));
                },
                enumerable: true,
                configurable: true
            },
            /** VAT excluded text */
            noTaxChargedVAT: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.noTaxChargedVAT?c=3ebfixtzoylgu", defaultMessage: "VAT \u2022 (excluded)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Sales tax excluded text */
            noTaxChargedSalesTaxExcluded: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.noTaxChargedSalesTaxExcluded?c=54n7sy09q43b", defaultMessage: "Sales Tax \u2022 (excluded)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Sales tax 0% text */
            noTaxChargedSalesTaxZero: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.noTaxChargedSalesTaxZero?c=9498oc59hmu6", defaultMessage: "Sales Tax \u2022 (0%)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Total row label */
            total: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.total?c=3eypzcojfaejd", defaultMessage: "Total" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip explaining tax exclusion */
            toolTip: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.toolTip?c=1i52x87tf50au", defaultMessage: "Ensure your Tax ID is correct in your Payment Details to exclude tax." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Description wrapped in parentheses for collapsed view */
            descriptionCollapsedFormat: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (description: string): string => __intl__.intl().formatMessage({ id: "PlanSummaryDetails.descriptionCollapsedFormat?c=2y9l6n44qbayf", defaultMessage: "({description})" }, { description: description }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
